import { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { GroupsSection } from './GroupsSection';
import { CompulsoriesSection } from './CompulsoriesSection';
import { ProgrammeTimingSection } from './ProgrammeTimingSection';
import { JudgesNotesSection } from './JudgesNotesSection';
import { GradeEventTimingSection } from './GradeEventTimingSection';
import { HelpCircle, Info } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  roundOneContentTypes, 
  roundTwoContentTypes, 
  classifications, 
  classificationLabel,
  programmeContentTypes 
} from './constants';

export function GradeForm({ 
  form, 
  index, 
  isEditMode = false, 
  disciplines, 
  figures, 
  dances, 
  sharedData, 
  updateGradeForm, 
  updateGradeFormGroup,
  formatFigureDisplay,
  schedules = [],
  currentUser = null,
  // Only rendered in edit mode (grade must exist to upsert its
  // event_timings row). onTimingChange receives { row, scheduleWide }
  // and the parent carries it into the Save handler.
  onTimingChange = null
}) {
  const [classificationHelpOpen, setClassificationHelpOpen] = useState(false);
  const [optionsHelpOpen, setOptionsHelpOpen] = useState(false);
  const [judgingHelpOpen, setJudgingHelpOpen] = useState(false);
  
  // Get selected discipline info
  const selectedDiscipline = disciplines.find(d => d.discipline_id === sharedData.discipline_id);
  const isFiguresDiscipline = selectedDiscipline?.type === 'Figures';
  const isTeamsDiscipline = selectedDiscipline?.competitor_type === 'Teams';
  const isOptional = index > 0;
  const gradeLabel = isOptional ? `Grade ${index + 1} (Optional)` : `Grade ${index + 1}`;
  
  // Check if classification should be locked to "Other"
  const selectedSchedule = schedules.find(s => s.schedule_id === sharedData?.schedule_id);
  const isSpecialEventSchedule = selectedSchedule?.schedule_type === 'Special Event Schedule';
  const isStandardUser = currentUser?.role === 'standard';
  const lockClassificationToOther = isSpecialEventSchedule || isStandardUser;
  
  // If locked to Other, ensure classification is set to Other
  if (lockClassificationToOther && form.classification !== 'Other') {
    // Auto-update to Other when conditions are met
    setTimeout(() => updateGradeForm(index, 'classification', 'Other'), 0);
  }

  // Judging-System auto-default: when the discipline OR classification
  // *changes*, push the judging_system to "White System" if the new
  // discipline has type=Figures OR the new classification is "Other".
  // Tracked via a ref so the user's manual choice sticks once they've
  // touched the field — we only auto-fill on actual transitions, not
  // every re-render.
  const lastDriverRef = useRef({
    discipline_id: sharedData?.discipline_id,
    classification: form.classification,
    initialised: false,
  });
  useEffect(() => {
    const prev = lastDriverRef.current;
    const driver = {
      discipline_id: sharedData?.discipline_id,
      classification: form.classification,
    };
    // Skip the very first render — we don't want to overwrite a saved
    // value when the form initially loads in edit mode.
    if (!prev.initialised) {
      lastDriverRef.current = { ...driver, initialised: true };
      return;
    }
    const changed = (
      prev.discipline_id !== driver.discipline_id
      || prev.classification !== driver.classification
    );
    if (changed) {
      const shouldDefaultWhite = isFiguresDiscipline || driver.classification === 'Other';
      if (shouldDefaultWhite && form.judging_system !== 'White System') {
        updateGradeForm(index, 'judging_system', 'White System');
      }
    }
    lastDriverRef.current = { ...driver, initialised: true };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedData?.discipline_id, form.classification, isFiguresDiscipline]);
  
  return (
    <div className={`border rounded-lg p-4 ${!isEditMode ? 'bg-stone-50' : ''} ${isEditMode ? 'w-full max-w-full' : ''}`}>
      {!isEditMode && (
        <div className="mb-3 pb-2 border-b">
          <h4 className="font-semibold text-lg" style={{ color: '#2D4A3E' }}>{gradeLabel}</h4>
        </div>
      )}
      
      {/* Name */}
      <div className="mb-4">
        <Label>Name {!isOptional && '*'}</Label>
        <Input 
          value={form.name} 
          onChange={(e) => updateGradeForm(index, 'name', e.target.value)} 
          required={!isOptional}
          placeholder={isOptional ? "Leave blank to skip" : "e.g., Primary Ladies"}
          data-testid={`grade-name-${index}`}
        />
      </div>
      
      {/* Classification */}
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <Label>Grade Classification</Label>
          <button
            type="button"
            onClick={() => setClassificationHelpOpen(true)}
            className="text-stone-400 hover:text-stone-600 transition-colors"
            title="What are Grade Classifications?"
          >
            <HelpCircle className="h-4 w-4" />
          </button>
        </div>
        <div className="flex flex-wrap gap-4 mt-2">
          {classifications.map(c => (
            <label 
              key={c} 
              className={`flex items-center gap-2 ${
                lockClassificationToOther && c !== 'Other' 
                  ? 'cursor-not-allowed opacity-50' 
                  : 'cursor-pointer'
              }`}
            >
              <input 
                type="radio" 
                name={`classification-${index}`} 
                value={c} 
                checked={form.classification === c} 
                onChange={(e) => updateGradeForm(index, 'classification', e.target.value)}
                disabled={lockClassificationToOther && c !== 'Other'}
              />
              <span className="text-sm">{classificationLabel(c)}</span>
            </label>
          ))}
        </div>
        {lockClassificationToOther && (
          <p className="text-xs text-amber-600 mt-1">
            {isStandardUser 
              ? 'Standard users can only create grades with "Local/Regional" classification.'
              : 'Special Event Schedules use "Local/Regional" classification.'}
          </p>
        )}
      </div>
      
      {/* Grade Options - Two columns of three */}
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <Label>Grade Options</Label>
          <button
            type="button"
            onClick={() => setOptionsHelpOpen(true)}
            className="text-stone-400 hover:text-stone-600 transition-colors"
            title="What are Grade Options?"
          >
            <HelpCircle className="h-4 w-4" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={form.group_by_grade} onCheckedChange={(val) => updateGradeForm(index, 'group_by_grade', val)} />
            <span className="text-sm">Group By Grade</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={form.skate_mf_together} onCheckedChange={(val) => updateGradeForm(index, 'skate_mf_together', val)} />
            <span className="text-sm">Skate M/F together?</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={form.is_beginner_for_fees}
              onCheckedChange={(val) => updateGradeForm(index, 'is_beginner_for_fees', val)}
              data-testid={`is-beginner-for-fees-${index}`}
            />
            <span className="text-sm">Beginner Grade for entry fees</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={form.judge_mf_together} onCheckedChange={(val) => updateGradeForm(index, 'judge_mf_together', val)} />
            <span className="text-sm">Judge M/F together?</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={form.local_regional_only} onCheckedChange={(val) => updateGradeForm(index, 'local_regional_only', val)} />
            <span className="text-sm">Local/Regional Only</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={form.can_use_panels} onCheckedChange={(val) => updateGradeForm(index, 'can_use_panels', val)} />
            <span className="text-sm">Grade uses panels</span>
          </label>
        </div>
      </div>

      {/* Judging System — two columns: radios on the left, B Mark
          if no RollArt? checkbox on the right (visible only when
          White System is selected, sitting directly under the
          "Grade uses panels" checkbox from the row above). */}
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <Label>Judging System</Label>
          <button
            type="button"
            onClick={() => setJudgingHelpOpen(true)}
            className="text-stone-400 hover:text-stone-600 transition-colors"
            title="What is the Judging Type Option?"
            data-testid={`judging-system-help-${index}`}
          >
            <HelpCircle className="h-4 w-4" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-2">
          <div className="flex flex-wrap gap-4">
            {['RollArt', 'White System'].map(opt => (
              <label key={opt} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`judging-system-${index}`}
                  value={opt}
                  checked={form.judging_system === opt}
                  onChange={(e) => updateGradeForm(index, 'judging_system', e.target.value)}
                  data-testid={`judging-system-${index}-${opt.toLowerCase().replace(' ', '-')}`}
                />
                <span className="text-sm">{opt}</span>
              </label>
            ))}
          </div>
          {form.judging_system === 'White System' && (
            <label
              className="flex items-center gap-2 cursor-pointer"
              data-testid={`b-mark-no-rollart-wrapper-${index}`}
            >
              <Checkbox
                checked={form.b_mark_no_rollart}
                onCheckedChange={(val) => updateGradeForm(index, 'b_mark_no_rollart', val)}
                data-testid={`b-mark-no-rollart-${index}`}
              />
              <span className="text-sm">B Mark if no RollArt?</span>
            </label>
          )}
        </div>
      </div>
      
      {/* Event Timing — per-grade timing values that live in the
          event_timings collection. Edit-mode only (the row keys off
          the grade's id which doesn't exist until the grade is
          created). */}
      {isEditMode && form.grade_id && (
        <GradeEventTimingSection
          gradeId={form.grade_id}
          classification={form.classification}
          localRegionalOnly={form.local_regional_only}
          disciplineName={selectedDiscipline?.discipline}
          roundOneContentType={form.round_one_content_type}
          programmeTime={
            form.round_one_time_min && form.round_one_time_max
              ? `${form.round_one_time_min}–${form.round_one_time_max}`
              : (form.round_one_time_min || form.round_one_time_max || '')
          }
          onChange={onTimingChange}
        />
      )}
      
      {/* Round One Content Type */}
      <div className="mb-4">
        <Label>Round One Content Type *</Label>
        <select 
          className="w-full border rounded-lg px-3 py-2 mt-1"
          value={form.round_one_content_type}
          onChange={(e) => updateGradeForm(index, 'round_one_content_type', e.target.value)}
          required
          data-testid={`round-one-type-${index}`}
        >
          <option value="">Select Content Type</option>
          {roundOneContentTypes.map(t => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>
      
      {/* Round Two Content Type */}
      <div className="mb-4">
        <Label>Round Two Content Type</Label>
        <select 
          className="w-full border rounded-lg px-3 py-2 mt-1"
          value={form.round_two_content_type}
          onChange={(e) => updateGradeForm(index, 'round_two_content_type', e.target.value)}
          data-testid={`round-two-type-${index}`}
        >
          {roundTwoContentTypes.map(t => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>

      {/* Team-size constraints (Team grades only). Reserves don't
          count toward min/max skaters. ``Max reserves`` blank =
          unlimited. */}
      {isTeamsDiscipline && (
        <div className="mb-4 rounded-lg border bg-stone-50 p-3">
          <Label className="mb-2 block">Team size (excluding reserves)</Label>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor={`min-skaters-${index}`} className="text-xs text-stone-600">
                Min skaters
              </Label>
              <Input
                id={`min-skaters-${index}`}
                type="number"
                min={1}
                value={form.min_skaters ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  updateGradeForm(index, 'min_skaters', v === '' ? '' : parseInt(v, 10));
                }}
                placeholder="4"
                data-testid={`min-skaters-${index}`}
              />
            </div>
            <div>
              <Label htmlFor={`max-skaters-${index}`} className="text-xs text-stone-600">
                Max skaters
              </Label>
              <Input
                id={`max-skaters-${index}`}
                type="number"
                min={1}
                value={form.max_skaters ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  updateGradeForm(index, 'max_skaters', v === '' ? '' : parseInt(v, 10));
                }}
                placeholder="unlimited"
                data-testid={`max-skaters-${index}`}
              />
            </div>
            <div>
              <Label htmlFor={`max-reserves-${index}`} className="text-xs text-stone-600">
                Max reserves
              </Label>
              <Input
                id={`max-reserves-${index}`}
                type="number"
                min={0}
                value={form.max_reserves ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  updateGradeForm(index, 'max_reserves', v === '' ? '' : parseInt(v, 10));
                }}
                placeholder="unlimited"
                data-testid={`max-reserves-${index}`}
              />
            </div>
          </div>
          <p className="text-xs text-stone-500 mt-2">
            Set <strong>Min</strong> to 1 for Showcase-style events that allow solo entries.
          </p>
          <p className="text-xs text-stone-500 mt-1">
            Mini-Precision — total number of skaters including reserves must not exceed eight.
          </p>
          <p className="text-xs text-stone-500 mt-1">
            For maximum skaters/reserves — leave blank to show as unlimited.
          </p>
        </div>
      )}
      
      {/* Groups Content Form */}
      {form.round_one_content_type === 'Groups' && (
        <GroupsSection 
          form={form}
          index={index}
          figures={figures}
          updateGradeForm={updateGradeForm}
          updateGradeFormGroup={updateGradeFormGroup}
          formatFigureDisplay={formatFigureDisplay}
        />
      )}
      
      {/* Compulsories Content Form */}
      {form.round_one_content_type === 'Compulsories' && (
        <CompulsoriesSection 
          form={form}
          index={index}
          dances={dances}
          updateGradeForm={updateGradeForm}
        />
      )}
      
      {/* Programme Timings for Round 1 - Show for programme-based content types */}
      {programmeContentTypes.includes(form.round_one_content_type) && (
        <ProgrammeTimingSection 
          form={form}
          index={index}
          roundNum={1}
          contentType={form.round_one_content_type}
          dances={dances}
          updateGradeForm={updateGradeForm}
        />
      )}
      
      {/* Programme Timings for Round 2 - Show for programme-based content types */}
      {form.round_two_content_type !== 'No Round Two' && programmeContentTypes.includes(form.round_two_content_type) && (
        <ProgrammeTimingSection 
          form={form}
          index={index}
          roundNum={2}
          contentType={form.round_two_content_type}
          dances={dances}
          updateGradeForm={updateGradeForm}
        />
      )}
      
      {/* Judges Notes - Only show when a valid content type is selected */}
      {form.round_one_content_type && (
        <JudgesNotesSection 
          form={form}
          index={index}
          updateGradeForm={updateGradeForm}
        />
      )}
      
      {/* Classification Help Dialog */}
      <AlertDialog open={classificationHelpOpen} onOpenChange={setClassificationHelpOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-blue-500" />
              What are Grade Classifications?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left">
              These are used to classify grades within our standard schedules - for example, SkateNZ is a mix of WorldSkate grades and SkateNZ grades. Standard users can only create grades classified as "Other".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>Okay</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Grade Options Help Dialog */}
      <AlertDialog open={optionsHelpOpen} onOpenChange={setOptionsHelpOpen}>
        <AlertDialogContent className="max-w-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-blue-500" />
              What are Grade Options?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left space-y-3">
              <p>These are options for the grade which help the system build the order of skate, programme, judges documents with less manual intervention. These options are:</p>
              <ul className="list-disc pl-5 space-y-2 text-sm">
                <li><strong>Group By Grade</strong> - Events where they are grouped by the grade rather than the discipline in documents and often on the day. Example - some open days use Beginner 1 Figures, Freeskating, Solo Dance are grouped under the grade, Beginner 1.</li>
                <li><strong>B Mark if No RollArt?</strong> - This event requires an artistic impression mark if we are not using the RollArt system.</li>
                <li><strong>Local/Regional Only</strong> - Grades on special event schedules, say additional events for beginners at an open weekend. Also includes the "Up to Regionals Only" grades in the SkateNZ schedule.</li>
                <li><strong>Skate M/F together?</strong> - Do we run the events together/back to back? Usually Yes for most events.</li>
                <li><strong>Judge M/F together?</strong> - Do we judge this as a single event regardless of the gender of the skater? Usually no, sometimes used for special events.</li>
                <li><strong>Grade Uses Panels</strong> - Mainly used for figures; sometimes for special events. Does this grade have the option of running events on panels?</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>Okay</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Judging System Help Dialog */}
      <AlertDialog open={judgingHelpOpen} onOpenChange={setJudgingHelpOpen}>
        <AlertDialogContent className="max-w-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-blue-500" />
              What is the Judging Type Option?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left">
              Events in artistic rollerskating are mostly judged using the RollArt judging system. All figure/loop events and any bespoke events organised outside the standard gradings are most likely to use the White System of judging.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>Okay</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
